/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  EnvironmentBuild,
  EnvironmentBuild$inboundSchema,
} from "./environmentbuild.js";
import {
  EnvironmentKind,
  EnvironmentKind$inboundSchema,
} from "./environmentkind.js";
import {
  EnvironmentRegion,
  EnvironmentRegion$inboundSchema,
} from "./environmentregion.js";
import {
  EnvironmentRuntime,
  EnvironmentRuntime$inboundSchema,
} from "./environmentruntime.js";

export type Environment = {
  /**
   * The unique identifier of the environment, generated by Teral.
   *
   * @remarks
   */
  id: string;
  /**
   * Human-readable slug of the environment, unique within its app.
   *
   * @remarks
   */
  slug: string;
  /**
   * Human-readable description of this environment.
   *
   * @remarks
   * Empty string if none was provided.
   */
  description: string;
  /**
   * The deployment lifecycle role of an environment.
   *
   * @remarks
   *
   * - `production`: Deployments serve production traffic, support promotion and rollback, and cannot be stopped.
   * - `preview`: Deployments can be stopped and started and are eligible for preview lifecycle automation.
   */
  kind: EnvironmentKind;
  /**
   * Whether delete protection is enabled for this environment.
   *
   * @remarks
   * When true, the environment cannot be deleted until protection is disabled.
   */
  deleteProtection: boolean;
  /**
   * Unix timestamp in milliseconds when the environment was created.
   *
   * @remarks
   */
  createdAt: number;
  /**
   * Unix timestamp in milliseconds when the environment was last updated.
   *
   * @remarks
   * Omitted if the environment has never been updated.
   */
  updatedAt?: number | undefined;
  /**
   * Runtime settings that control how the container runs.
   *
   * @remarks
   * Omitted until the environment has runtime settings.
   */
  runtime?: EnvironmentRuntime | undefined;
  /**
   * Build settings that control how the app is built.
   *
   * @remarks
   * Omitted until the environment has build settings.
   */
  build?: EnvironmentBuild | undefined;
  /**
   * Per-region deployment settings for this environment.
   *
   * @remarks
   * Empty until regional settings are configured.
   */
  regions?: Array<EnvironmentRegion> | undefined;
};

/** @internal */
export const Environment$inboundSchema: z.ZodType<
  Environment,
  z.ZodTypeDef,
  unknown
> = z.object({
  id: z.string(),
  slug: z.string(),
  description: z.string(),
  kind: EnvironmentKind$inboundSchema,
  deleteProtection: z.boolean(),
  createdAt: z.number().int(),
  updatedAt: z.number().int().optional(),
  runtime: EnvironmentRuntime$inboundSchema.optional(),
  build: EnvironmentBuild$inboundSchema.optional(),
  regions: z.array(EnvironmentRegion$inboundSchema).optional(),
});

export function environmentFromJSON(
  jsonString: string,
): SafeParseResult<Environment, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => Environment$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'Environment' from JSON`,
  );
}
